/*
 * Ported from Extend's document viewers (https://www.extend.ai/ui,
 * github.com/extend-hq/ui). Copyright (c) CrowdView Inc (Extend), MIT License.
 */
import { MinusCircleIcon } from "@phosphor-icons/react/dist/csr/MinusCircle";
import { PlusCircleIcon } from "@phosphor-icons/react/dist/csr/PlusCircle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import { Stack } from "@volca/ui/components/ui/stack";
import { ToolbarIconButton } from "./viewer-chrome";

/** A zoom level as a fraction (1 is 100%) or a fit mode. */
export type ZoomValue = number | string;

const percent = (value: number) => `${Math.round(value * 100)}%`;

/**
 * Zoom out, the level (a list of fit modes and percentages), zoom in. The
 * current level is listed even when it is not one of the steps, as it is
 * after a fit mode or a pinch.
 */
export const ZoomControl = ({
  current,
  level,
  steps,
  modes = [],
  disabled,
  onChange,
}: {
  /** The zoom being shown, as a fraction. */
  current: number;
  /** What was chosen: a fit mode's value, or a fraction. */
  level: ZoomValue;
  /** The fractions offered, smallest first. */
  steps: readonly number[];
  modes?: readonly { value: string; label: string }[];
  disabled?: boolean;
  onChange: (level: ZoomValue) => void;
}) => {
  const rounded = Number(current.toFixed(2));
  const options = steps.includes(rounded)
    ? steps
    : [...steps, rounded].sort((a, b) => a - b);
  const mode = modes.find((item) => item.value === level);
  const value = mode ? mode.value : String(rounded);
  const smaller = [...steps].reverse().find((step) => step < rounded - 0.001);
  const larger = steps.find((step) => step > rounded + 0.001);

  return (
    <Stack direction="row" align="center" gap={1} className="flex-none">
      <ToolbarIconButton
        label="Zoom out"
        disabled={disabled || smaller === undefined}
        onClick={() => smaller !== undefined && onChange(smaller)}
      >
        <MinusCircleIcon aria-hidden />
      </ToolbarIconButton>
      <Select
        value={value}
        onValueChange={(next) => {
          if (next === null) return;
          const chosen = String(next);
          onChange(
            modes.some((item) => item.value === chosen)
              ? chosen
              : Number(chosen),
          );
        }}
        disabled={disabled}
        modal={false}
      >
        <SelectTrigger size="sm" className="w-24" aria-label="Zoom level">
          <SelectValue>{percent(current)}</SelectValue>
        </SelectTrigger>
        <SelectContent align="end">
          {modes.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
          {options.map((step) => (
            <SelectItem key={step} value={String(step)}>
              {percent(step)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ToolbarIconButton
        label="Zoom in"
        disabled={disabled || larger === undefined}
        onClick={() => larger !== undefined && onChange(larger)}
      >
        <PlusCircleIcon aria-hidden />
      </ToolbarIconButton>
    </Stack>
  );
};
